import React, {
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback
} from 'react'
import { GridItem } from '@zuto/core/src/grid'
import * as yup from 'yup'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import debounce from 'lodash.debounce'
import { fireReactGAEvent, handlePrevButtonClick, questionFontStyle } from '../utils'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import QuestionComponentProps from '../../../Questions/question-component-props'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import MobileNumberValidator from '../../../validators/MobileNumberValidator'
import { EmailAddressValidatorLookupService, displayClickableEmail } from '../../../validators/EmailAddressValidatorLookupService'
import { AttributionContext } from '../../../context'
import { Loading } from '@zuto/molecules'
import getConfig from 'next/config'
import LookupService from '../../../services/lookup-service'
import EmailAddressValidator from '../../../validators/EmailAddressValidator'


const fieldValues = {
  contactNumber: '',
  emailAddress: ''
}

type FieldKey = keyof typeof fieldValues
type ValidationSchemaKey = 'contactNumber' | 'emailAddress'


const ContactInfoQuestion = ({
  existingAnswers,
  disablePrevious,
  errors: existingErrors,
  bouncerError
}: QuestionComponentProps): JSX.Element => {
  const { dispatch, featureSwitches } = useContext(ApplicationFormContext)
  const attribution = useContext(AttributionContext)
  const { publicRuntimeConfig } =  getConfig()
  const lookupService = LookupService(publicRuntimeConfig)
  const { bouncerV1 } = featureSwitches ?? {}
  const determinedFont = questionFontStyle
  const [loading, setLoading] = useState(false)
  const [, setFocus] = useState<FieldKey | 'nextButton' | 'previousButton' | null>(null)

  const [values, setValues] = useState<{ [key in FieldKey]: string }>(existingAnswers ?? {
    contactNumber: '',
    emailAddress: ''
  })

  const [showErrors, setShowErrors] = useState<{ [key in FieldKey]: boolean }>({
    contactNumber: false,
    emailAddress: false
  })

  const [dirtyFields, setDirtyFields] = useState<{ [key in FieldKey]: boolean }>({
    contactNumber: !!existingAnswers?.contactNumber,
    emailAddress: !!existingAnswers?.emailAddress
  })

  const [emailValidationError, setEmailValidationError] = useState<string>(bouncerError ?? '')

  const validationSchemas: {
    [key in ValidationSchemaKey]: yup.BaseSchema
  } = {
    contactNumber: MobileNumberValidator().getValidator(),
    emailAddress: bouncerV1 ? EmailAddressValidatorLookupService(emailValidationError).getValidator() : EmailAddressValidator().getValidator()
  }

  const validate = (key: ValidationSchemaKey, value: string): string => {
    try {
      validationSchemas[key].validateSync({ [key]: value })
    } catch (untypedError: unknown) {
      const errorObject = untypedError as yup.ValidationError
      return errorObject.message
    }
    return ''
  }

  const contactNumberError = useMemo(() => validate('contactNumber', values.contactNumber), [values.contactNumber])
  const emailAddressError = useMemo(() => validate('emailAddress', values.emailAddress), [values.emailAddress, emailValidationError])

  const errors = [contactNumberError, emailAddressError]

  const debouncedSetShowError = (key: FieldKey) => {
    setShowErrors((previous) => ({ ...previous, [key]: true }))
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Validation Error',
      label: key,
      nonInteraction: true
    })
  }

  const debounceMap : Record<FieldKey, () => void> = useMemo(
    () => ({
      contactNumber: debounce(() => debouncedSetShowError('contactNumber'), 1500),
      emailAddress: debounce(() => debouncedSetShowError('emailAddress'), 1500)
    }),
    []
  )

  const setShouldShowError = useCallback((key: FieldKey, errorMessage: string, isDirty: boolean, showError: boolean): void => {
    if (isDirty && errorMessage) {
      if (showError) {
        setShowErrors((previous) => ({ ...previous, [key]: true }))
      } else {
        // We'll only debounce if no error is currently displayed.
        debounceMap[key]()
      }
    } else {
      setShowErrors((previous) => ({ ...previous, [key]: false }))
    }
  }, [])

  useEffect(
    () => setShouldShowError(
    'contactNumber',
    contactNumberError,
    dirtyFields.contactNumber,
    showErrors.contactNumber
    || (!!existingErrors?.contactNumber && !!existingAnswers.contactNumber)
  ),
  [values.contactNumber, contactNumberError, dirtyFields.contactNumber, showErrors.contactNumber]
  )
  useEffect(
    () => setShouldShowError(
    'emailAddress',
    emailAddressError || emailValidationError,
    dirtyFields.emailAddress,
    showErrors.emailAddress || !!emailValidationError
    || (!!existingErrors?.emailAddress && !!existingAnswers?.emailAddress)
  ),
  [values.emailAddress, emailAddressError, dirtyFields.emailAddress, showErrors.emailAddress])

  const displayNextButton: boolean = useMemo(
    () => Object.values(dirtyFields).every((isDirty) => isDirty) && errors.every((error) => !error),
    [dirtyFields, errors]
  )

  const onChange = useCallback((key: FieldKey, value: string) => {
    if (key === 'emailAddress' && bouncerV1) setEmailValidationError('')
    if (!dirtyFields[key]) setDirtyFields((previous) => ({ ...previous, [key]: true }))
    setValues((previous) => ({ ...previous, [key]: value }))
  }, [])

  const runDisplayClickableEmail = useCallback(
    (emailAddressError: string) => displayClickableEmail(emailAddressError, onChange)
  , [onChange])

  const dispatchQuestionAnswered = () => {
    setEmailValidationError('')
    dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.CONTACT_INFO,
      value: values,
      errors: showErrors,
      ...(bouncerV1 && {bouncerError: ''})
    })
  }

  const nextQuestion = async () => {
    if (displayNextButton) {
      if (!bouncerV1) return dispatchQuestionAnswered()
      try {
        setLoading(true)
        await lookupService.validateEmail(values.emailAddress, attribution.source)
        dispatchQuestionAnswered()
      } catch(err) {
        const error = err as { message: string }
        setLoading(false)
        setEmailValidationError(error?.message)
        !(error?.message) && dispatchQuestionAnswered()
      }
    }
  }

  const previousQuestion = () => handlePrevButtonClick(dispatch, {
    name: QuestionKey.CONTACT_INFO,
    value: values,
    errors: showErrors,
    ...(bouncerV1 && {bouncerError: emailValidationError})
  })

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']} mb={5} minHeight="255px">
        <AppFormQuestion
          questionKey={QuestionKey.CONTACT_INFO}
          mt={3}
          mb={3}
          textStyle={determinedFont}
        >
          We&apos;ll keep you updated
        </AppFormQuestion>
        <MuiTextField
          onFocus={() => setFocus('contactNumber')}
          autoComplete="tel"
          data-hj-suppress=""
          defaultValue={existingAnswers?.contactNumber}
          errorMessage={showErrors.contactNumber ? contactNumberError : undefined}
          id="contactNumber"
          inputMaxLength={20}
          inputType="tel"
          inputVariant="outlined"
          label="Mobile number"
          mb="34px"
          multipleInputs
          name="contactNumber"
          onChangeHandler={(event) => onChange('contactNumber', event.currentTarget.value)}
          testId="contactNumber"
        />
        <MuiTextField
          onFocus={() => setFocus('emailAddress')}
          autoComplete="email"
          data-hj-suppress=""
          {... bouncerV1 ? 
            {value: values.emailAddress} // Controlled component
            : {defaultValue: existingAnswers?.emailAddress}}
          errorMessage={showErrors.emailAddress ? runDisplayClickableEmail(emailAddressError) : undefined}
          id="emailAddress"
          inputMaxLength={100}
          inputType="email"
          inputVariant="outlined"
          label="Email address"
          mb="34px"
          name="emailAddress"
          onChangeHandler={(event) => onChange('emailAddress', event.currentTarget.value)}
          testId="emailInput"
        />
        {loading && <Loading />}
      </GridItem>
      <NavigationButtons
        nextHandler={nextQuestion}
        previousHandler={previousQuestion}
        disableNext={!displayNextButton}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        nextButtonProps={{ onFocus: () => setFocus('nextButton') }}
        previousButtonProps={{ onFocus: () => setFocus('previousButton') }}
      />
    </>
  )
}

export default withFade(withQuestion(ContactInfoQuestion))
